/**
 * use-ops-mutations — 运营管理（首页推荐 / Banner / FAQ）写动作 mutation 集
 *
 * 把 ADM-OPS-S001~S005 的写动作统一封装为 useMutation：
 *   - create（POST）：banners（A2 §3.2，slug 撞行报冲突而非静默改既有位）
 *   - upsert（PUT）：home-sections / banners / banner slides —— requireRole root/admin/operator
 *   - delete（DELETE）：home-sections / banners / banner slides —— requireRole root/admin（operator/viewer 拒）
 *   - FAQ canonical（#221 REST CRUD）：faqs / faq-categories 的 create(POST)/update(PATCH)/delete(DELETE)
 *     —— 写全 root/admin/operator（含 DELETE，故页面用 useCanUpsertOps 而非 admin-only useCanDeleteOps 门禁）
 * 成功后按 dev-docs 契约 invalidate 对应 queryKey（['home-blocks'] / ['banners'] / ['faqs'] / ['faq-categories']），
 * 失败抛 AdminApiError → 调用方按 code 弹错误 toast，绝不假成功（checklist #2）。
 *
 * 注意：slide 写动作落 ['banners']（slide 计数变化会影响 banner 卡片 N+1 张数），
 * 同时也需 refetch 当前 banner 的 slide 列表（由调用方持有的 useSlides 查询 key 一并失效）。
 */

import type {
  BannerResponse,
  BannerSlideResponse,
  CreateBannerRequest,
  CreateBannerSlideRequest,
  CreateFaqCategoryRequest,
  CreateFaqRequest,
  CreateHomeSectionNovelRequest,
  FaqCategoryResponse,
  FaqResponse,
  HomeSectionNovelResponse,
  HomeSectionResponse,
  UpdateBannerRequest,
  UpdateBannerSlideRequest,
  UpdateFaqCategoryRequest,
  UpdateFaqRequest,
  UpdateHomeSectionNovelRequest,
  UpdateHomeSectionRequest,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { opsApi } from '~/hooks/use-ops';
import { useAuthStore } from '~/stores/auth-store';
import { toast } from '~/ui/Toast';

/**
 * 统一错误 toast：写动作失败（含响应丢失/超时/解码失败）→ 报错不假成功（checklist #2）。
 * 优先回显后端 AdminApiError 中文化 message，回退调用方给的兜底前缀。
 */
export function errorToast(err: unknown, fallbackPrefix: string): void {
  const msg = err instanceof Error && err.message ? err.message : fallbackPrefix;
  toast(`${fallbackPrefix} · ${msg}`, { tone: 'neg', icon: 'IconAlert' });
}

/** 统一成功 toast（写动作 onSuccess 调用方用）。 */
export function successToast(message: string): void {
  toast(message, { tone: 'pos', icon: 'IconCheck' });
}

/** upsert 写角色闭集（root/admin/operator）；viewer 只读——前端防线，后端 requireRole 才是权威 */
export function useCanUpsertOps(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

/**
 * 运营弹窗场景写角色闭集（root/admin）——**比其余 ops 模块的 upsert 档更窄**。
 *
 * canonical `POST/PATCH/DELETE /ops/popup-scenes` 三条全是 `requireRole('root','admin')`
 * （dev-docs `02-API契约/A2-运营配置.md` §5.2~§5.4「admin+ mfa」，正文明写「本页弹窗写权限统一
 * 收窄至 root / admin，operator / viewer 只读」）。若沿用 `useCanUpsertOps`（含 operator），
 * operator 会看到可点的上下架按钮、点完吃 403 —— 前端 gate 必须与后端 requireRole 同档。
 */
export function useCanWritePopupScenes(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}

/** delete 写角色闭集（root/admin）；operator 也不可删——对齐 server DELETE requireRole('root','admin') */
export function useCanDeleteOps(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}

/** ops 写动作 mutation 集合（各自带成功后 invalidate；onError 交调用方弹错误 toast） */
export function useOpsMutations() {
  const queryClient = useQueryClient();
  // invalidate 全部 ops 读 key（home-blocks / banners / faqs 都带 appId 维度后缀 → 用前缀失效）
  const invalidate = (key: string) => queryClient.invalidateQueries({ queryKey: [key] });

  const updateSection = useMutation<
    HomeSectionResponse,
    Error,
    { id: string; body: UpdateHomeSectionRequest }
  >({
    mutationFn: ({ id, body }) => opsApi.updateSection(id, body),
    onSuccess: async () => {
      await invalidate('home-blocks');
    },
  });

  const createBanner = useMutation<BannerResponse, Error, CreateBannerRequest>({
    mutationFn: (body) => opsApi.createBanner(body),
    onSuccess: async () => {
      await invalidate('banners');
    },
  });

  const updateBanner = useMutation<
    BannerResponse,
    Error,
    { id: string; body: UpdateBannerRequest }
  >({
    mutationFn: ({ id, body }) => opsApi.updateBanner(id, body),
    onSuccess: async () => {
      await invalidate('banners');
    },
  });

  const createSlide = useMutation<
    BannerSlideResponse,
    Error,
    { bannerId: string; body: CreateBannerSlideRequest }
  >({
    mutationFn: ({ bannerId, body }) => opsApi.createSlide(bannerId, body),
    onSuccess: async (_dto, { bannerId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['banner-slides', bannerId] }),
        invalidate('banners'),
      ]);
    },
  });

  const updateSlide = useMutation<
    BannerSlideResponse,
    Error,
    { bannerId: string; slideId: string; body: UpdateBannerSlideRequest }
  >({
    mutationFn: ({ bannerId, slideId, body }) => opsApi.updateSlide(bannerId, slideId, body),
    onSuccess: async (_dto, { bannerId }) => {
      // slide 列表（详情抽屉读）+ banner 卡片张数（首页 ['banners'] N+1）均需刷新
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['banner-slides', bannerId] }),
        invalidate('banners'),
      ]);
    },
  });

  const deleteSlide = useMutation<{ ok: true }, Error, { bannerId: string; slideId: string }>({
    mutationFn: ({ bannerId, slideId }) => opsApi.deleteSlide(bannerId, slideId),
    onSuccess: async (_dto, { bannerId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['banner-slides', bannerId] }),
        invalidate('banners'),
      ]);
    },
  });

  // FAQ canonical（#221）：REST CRUD 取代旧 PUT upsert。create(POST)/update(PATCH)/delete(DELETE) 均落 ['faqs']。
  const createFaq = useMutation<FaqResponse, Error, CreateFaqRequest>({
    mutationFn: (body) => opsApi.createFaq(body),
    onSuccess: async () => {
      await invalidate('faqs');
    },
  });

  const updateFaq = useMutation<FaqResponse, Error, { id: string; body: UpdateFaqRequest }>({
    mutationFn: ({ id, body }) => opsApi.updateFaq(id, body),
    onSuccess: async () => {
      await invalidate('faqs');
    },
  });

  const deleteFaq = useMutation<{ id: string; deleted_at: string }, Error, string>({
    mutationFn: (id) => opsApi.deleteFaq(id),
    onSuccess: async () => {
      await invalidate('faqs');
    },
  });

  // FAQ 分类注册表写动作：create 落 ['faq-categories']；update/delete 还需刷 ['faqs']（分类名 / 可用性变化影响 FAQ 分组标签）。
  const createFaqCategory = useMutation<FaqCategoryResponse, Error, CreateFaqCategoryRequest>({
    mutationFn: (body) => opsApi.createFaqCategory(body),
    onSuccess: async () => {
      await invalidate('faq-categories');
    },
  });

  const updateFaqCategory = useMutation<
    FaqCategoryResponse,
    Error,
    { id: string; body: UpdateFaqCategoryRequest }
  >({
    mutationFn: ({ id, body }) => opsApi.updateFaqCategory(id, body),
    onSuccess: async () => {
      await Promise.all([invalidate('faq-categories'), invalidate('faqs')]);
    },
  });

  const deleteFaqCategory = useMutation<{ id: string; deleted_at: string }, Error, string>({
    mutationFn: (id) => opsApi.deleteFaqCategory(id),
    onSuccess: async () => {
      await Promise.all([invalidate('faq-categories'), invalidate('faqs')]);
    },
  });

  const invalidateSectionNovels = () =>
    queryClient.invalidateQueries({ queryKey: ['home-section-novels'] });

  const createSectionNovel = useMutation<
    HomeSectionNovelResponse,
    Error,
    { sectionId: string; body: CreateHomeSectionNovelRequest }
  >({
    mutationFn: ({ sectionId, body }) => opsApi.createSectionNovel(sectionId, body),
    onSuccess: async () => {
      await invalidateSectionNovels();
    },
  });

  const updateSectionNovel = useMutation<
    HomeSectionNovelResponse,
    Error,
    { sectionId: string; bindingId: string; body: UpdateHomeSectionNovelRequest }
  >({
    mutationFn: ({ sectionId, bindingId, body }) =>
      opsApi.updateSectionNovel(sectionId, bindingId, body),
    onSuccess: async () => {
      await invalidateSectionNovels();
    },
  });

  const deleteSectionNovel = useMutation<
    { id: string; deleted_at: string },
    Error,
    { sectionId: string; bindingId: string }
  >({
    mutationFn: ({ sectionId, bindingId }) => opsApi.deleteSectionNovel(sectionId, bindingId),
    onSuccess: async () => {
      await invalidateSectionNovels();
    },
  });

  return {
    updateSection,
    createBanner,
    updateBanner,
    createSlide,
    updateSlide,
    deleteSlide,
    createFaq,
    updateFaq,
    deleteFaq,
    createFaqCategory,
    updateFaqCategory,
    deleteFaqCategory,
    createSectionNovel,
    updateSectionNovel,
    deleteSectionNovel,
  };
}

export type OpsMutations = ReturnType<typeof useOpsMutations>;
